import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';
import { fetchHistoriaLimitu, type WpisHistoriiLimitu } from '@/lib/permissions-api';

/**
 * CO PORTAL ZMIENIAŁ W TYM LIMICIE.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 02.09.2026: „ikonka z zegarkiem, która pojawia się, jak ktoś zrobi zmiany przez
 *  portal — i będzie tam historia, co było ustawione wcześniej dla tej grupy". Powód jest
 *  praktyczny: dzisiejsza liczba nie mówi, czy ktoś ją wczoraj podniósł, a przy pytaniu „dlaczego
 *  automat wydaje 2" to jest pierwsza rzecz, którą trzeba wiedzieć.
 *
 *  ⚠️ WPIS MÓWI TEŻ, CZY ZMIANA DOJECHAŁA. „Zmienione u nas" i „maszyna już tak liczy" to dwie
 *  różne rzeczy — a przy limicie ta różnica decyduje o tym, ile automat naprawdę wyda.
 *
 *  ⚠️ „ZMIANY PORTALU", NIE „HISTORIA LIMITU". Zmian zrobionych przy maszynie w SPSUtilities tu
 *  nie ma i nigdy nie będzie: portal ich nie składał, więc nie ma o nich wiersza. Nazwa obiecuje
 *  dokładnie tyle, ile pokazujemy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function HistoriaLimitu({
  uprawnienieId,
  groupId,
  zakres,
  adresat,
  onClose,
}: {
  uprawnienieId: string;
  groupId: string;
  zakres: string;
  adresat: string;
  onClose: () => void;
}) {
  const { t } = useI18n();

  const dane = useQuery({
    queryKey: ['permissions', 'historia-limitu', uprawnienieId, groupId],
    queryFn: () => fetchHistoriaLimitu(uprawnienieId, groupId),
  });

  const wpisy = dane.data?.wpisy ?? [];

  return (
    <Dialog open onOpenChange={(otwarte) => !otwarte && onClose()}>
      <DialogContent
        className="w-[min(40rem,calc(100vw-2rem))]"
        title={t('hist.tytul')}
        description={`${zakres} · ${adresat}`}
      >
        {dane.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        ) : dane.isError ? (
          <p className="text-sm text-destructive">{(dane.error as Error).message}</p>
        ) : wpisy.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('hist.pusto')}</p>
        ) : (
          <ol className="space-y-2">
            {wpisy.map((w) => (
              <Wpis key={w.id} w={w} />
            ))}
          </ol>
        )}

        {/* ⚠️ GRANICA WIEDZY NA KOŃCU LISTY, nie w opisie okna — czyta się ją wtedy, gdy widać, ile jest wpisów. */}
        <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">{t('hist.zakresWiedzy')}</p>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Wpis({ w }: { w: WpisHistoriiLimitu }) {
  const { t } = useI18n();
  const dojechalo = w.potwierdzono !== null;

  return (
    <li className="rounded-md border border-border p-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-2">
        <Badge variant={w.rodzaj === 'ZDJETY' ? 'destructive' : w.rodzaj === 'USTAWIONY' ? 'success' : 'default'}>
          {t(`hist.rodzaj.${w.rodzaj}`)}
        </Badge>
        {/* Wartość, na jaką ustawiono. Po zdjęciu limitu nie ma jej i nie udajemy, że jest. */}
        <span className="font-medium tabular-nums">
          {w.maxQty === null || w.okno === null ? t('hist.bezWartosci') : `${w.maxQty} szt / ${w.okno}`}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">{czas(w.kiedy)}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {w.kto ?? t('hist.bezKonta')}
        {' · '}
        {/*
          ⚠️ STAN OPERACJI, NIE SAM CZAS ZAPISU. Wpis niepotwierdzony znaczy, że maszyna wciąż
          liczy po staremu — a to jest odpowiedź na „dlaczego automat wydaje inaczej".
        */}
        <span className={dojechalo ? undefined : 'text-[var(--warning)]'}>
          {dojechalo ? t('hist.dojechalo') : t('hist.wDrodze').replace('{stan}', w.stan)}
        </span>
      </p>
    </li>
  );
}

/** `2026-09-02 09:31` — bez sekund, bo to jest znacznik dla człowieka. */
function czas(iso: string): string {
  const d = new Date(iso);
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())} ${dwa(d.getHours())}:${dwa(d.getMinutes())}`;
}
